import React, { useRef, useState, useEffect } from 'react'
import { Download, Trash2 } from 'lucide-react'
import { GalleryItem as GalleryItemType, isStyleClipItem } from '../types'
import { ReactComponent as VideoDownloadIcon } from '../../../../svg/video-download.svg'
import { ReactComponent as EyeOffIcon } from '../../../../svg/eye-off.svg'
import { convertStorageUrlToCdn } from 'utils/common'
import { DeleteConfirmModal } from './DeleteConfirmModal'
import useUserStore from 'store/useUserStore'
import { ALLOW_EMAIL } from 'constant/query'

interface GalleryItemProps {
  item: GalleryItemType
  onDownload: (item: GalleryItemType) => void
  onVideoDownload?: (item: GalleryItemType) => void
  onDirectVideoCreate?: (item: GalleryItemType) => void
  onShowFullImage?: (item: GalleryItemType) => void // TODO: 삭제 - 테스트용 전체보기 핸들러
  onDelete?: (item: GalleryItemType) => void
}

// Intersection Observer 커스텀 훅
const useIntersectionObserver = (options = {}) => {
  const elementRef = useRef<HTMLDivElement>(null)
  const [isIntersecting, setIsIntersecting] = useState(false)
  const [hasBeenSeen, setHasBeenSeen] = useState(false)

  useEffect(() => {
    const element = elementRef.current
    if (!element) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsIntersecting(entry.isIntersecting)
        if (entry.isIntersecting && !hasBeenSeen) {
          setHasBeenSeen(true)
        }
      },
      {
        threshold: 0.1,
        rootMargin: '50px',
        ...options,
      },
    )

    observer.observe(element)

    return () => {
      observer.unobserve(element)
    }
  }, [hasBeenSeen, options])

  return { elementRef, isIntersecting, hasBeenSeen }
}

export const GalleryItem: React.FC<GalleryItemProps> = ({
  item,
  onDownload,
  onVideoDownload,
  onDirectVideoCreate,
  onShowFullImage, // TODO: 삭제 - 테스트용 전체보기 핸들러
  onDelete,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [imageLoaded, setImageLoaded] = useState(false)
  const [videoLoaded, setVideoLoaded] = useState(false)
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false)
  const { userInfo } = useUserStore()

  // Intersection Observer로 뷰포트 감지
  const { elementRef, hasBeenSeen } = useIntersectionObserver()

  const handleDownload = async () => {
    onDownload(item)
  }

  const handleVideoDownload = () => {
    if (onDirectVideoCreate) {
      onDirectVideoCreate(item)
    }
  }

  // TODO: 삭제 - 테스트용 전체보기 핸들러
  const handleShowFullImage = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (onShowFullImage) {
      onShowFullImage(item)
    }
  }

  const handleVideoClick = (e: React.MouseEvent) => {
    // 비디오 클릭 시 VideoDownloadModal 열기
    if (onVideoDownload) {
      e.stopPropagation()
      onVideoDownload(item)
    } else {
      if (!videoRef.current) return

      if (isPlaying) {
        videoRef.current.pause()
        setIsPlaying(false)
      } else {
        videoRef.current.play()
        setIsPlaying(true)
      }
    }
  }

  const handleItemClick = () => {
    if (onVideoDownload && item.status === 'completed') {
      onVideoDownload(item)
    }
  }

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation()
    setIsDeleteConfirmOpen(true)
  }

  const handleDeleteConfirm = () => {
    if (onDelete) {
      onDelete(item)
    }
  }

  const handleDeleteCancel = () => {
    setIsDeleteConfirmOpen(false)
  }

  const getStatusBadge = () => {
    if (item.status === 'queued' || item.status === 'processing') {
      return (
        <div className="absolute left-3 top-3 z-10 rounded bg-[rgba(39,39,39,0.7)] px-2 py-1">
          <span className="text-xs font-bold text-white">
            {item.status === 'processing' ? '생성중' : '생성중'}
          </span>
        </div>
      )
    }
    return null
  }

  const getDownloadButtons = () => {
    if (item.status === 'completed' && item.downloadUrl) {
      return (
        <div className="absolute bottom-3 right-3 flex gap-2">
          {/* 영상 다운로드 버튼 - 이미지 타입일 때만 표시 */}
          {item.type === 'style-shot' && onVideoDownload && (
            <button
              onClick={handleVideoDownload}
              className="rounded-md border border-[rgba(255,255,255,0.2)] bg-[rgba(55,59,66,0.7)] p-3 transition-colors hover:bg-[rgba(55,59,66,0.9)]"
            >
              <VideoDownloadIcon width={16} height={16} />
            </button>
          )}

          {/* 기본 다운로드 버튼 */}
          <button
            onClick={handleDownload}
            className="rounded-md border border-[rgba(255,255,255,0.2)] bg-[rgba(55,59,66,0.7)] p-3 transition-colors hover:bg-[rgba(55,59,66,0.9)]"
          >
            <Download size={16} className="text-white" />
          </button>
        </div>
      )
    }
    return null
  }

  // 스켈레톤 렌더링 함수
  const renderSkeleton = () => (
    <div className="flex h-full w-full animate-pulse items-center justify-center bg-gradient-to-br from-gray-300 to-gray-400">
      <div className="text-xs text-gray-500">Loading...</div>
    </div>
  )

  return (
    <div
      ref={elementRef}
      className="relative h-[248px] w-[160px] overflow-hidden rounded-[10px] border bg-[#141415]"
    >
      {item.status === 'queued' || item.status === 'processing' ? (
        // VideoCreationStep 스타일의 폴링 스켈레톤
        <div className="h-full w-full min-w-[168px] bg-gradient-to-br from-sky-200 via-indigo-300 to-purple-400">
          <div className="h-full w-full animate-skeleton bg-gradient-to-r from-transparent via-white/40 to-transparent"></div>
        </div>
      ) : item.imageUrl && !isStyleClipItem(item) ? (
        <>
          {!hasBeenSeen
            ? // 뷰포트에 들어오기 전까지 스켈레톤 표시
              renderSkeleton()
            : item.type === 'style-shot' && (
                <div className="relative h-full w-full">
                  <img
                    src={convertStorageUrlToCdn(item.imageUrl)}
                    alt="Style Shot"
                    className="h-full w-full cursor-pointer object-cover"
                    loading="lazy"
                    onLoad={() => setImageLoaded(true)}
                    onError={() => setImageLoaded(false)}
                    onClick={handleItemClick}
                    style={{
                      opacity: imageLoaded ? 1 : 0,
                      transition: 'opacity 0.3s ease-in-out',
                    }}
                  />
                  {ALLOW_EMAIL.includes(userInfo?.userInfo?.email ?? '') && (
                    <button
                      onClick={handleShowFullImage}
                      className="absolute right-3 top-3 rounded-md bg-[#FF4444] px-3 py-1.5 text-sm font-bold text-white shadow-lg transition-colors hover:bg-[#FF3333]"
                    >
                      전체보기
                    </button>
                  )}
                  {!imageLoaded && (
                    <div className="absolute inset-0">{renderSkeleton()}</div>
                  )}
                </div>
              )}
          {hasBeenSeen && getDownloadButtons()}
        </>
      ) : isStyleClipItem(item) && item.videoUrl ? (
        <>
          {!hasBeenSeen
            ? // 뷰포트에 들어오기 전까지 스켈레톤 표시
              renderSkeleton()
            : item.type === 'style-clip' && (
                <div
                  className="relative h-full w-full cursor-pointer"
                  onClick={handleVideoClick}
                >
                  <video
                    ref={videoRef}
                    src={
                      hasBeenSeen
                        ? convertStorageUrlToCdn(item.videoUrl ?? '')
                        : undefined
                    }
                    className="h-full w-full object-cover"
                    loop
                    preload={hasBeenSeen ? 'metadata' : 'none'}
                    onLoadedData={() => setVideoLoaded(true)}
                    onError={() => setVideoLoaded(false)}
                    style={{
                      opacity: videoLoaded ? 1 : 0,
                      transition: 'opacity 0.3s ease-in-out',
                    }}
                  />

                  {!videoLoaded && hasBeenSeen && (
                    <div className="absolute inset-0">{renderSkeleton()}</div>
                  )}

                  {videoLoaded && !isPlaying && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="rounded-full bg-black bg-opacity-50 p-3">
                        <div className="ml-1 h-0 w-0 border-b-[8px] border-l-[12px] border-r-0 border-t-[8px] border-b-transparent border-l-white border-t-transparent"></div>
                      </div>
                    </div>
                  )}
                </div>
              )}
          {hasBeenSeen && getDownloadButtons()}
        </>
      ) : isStyleClipItem(item) && item.status === 'failed' ? (
        <>
          <div className="relative flex h-full w-full flex-col items-center justify-center gap-[10px] bg-[#2E343D] p-5">
            <EyeOffIcon className="h-8 w-8 text-white" />
            <div className="text-center">
              <div className="mb-1 text-sm font-bold text-white">생성 실패</div>
              <div className="text-sm text-[#B6BBC6]">
                컨텐츠 정책에 위반된 것으로 보입니다.
              </div>
            </div>

            {onDelete && (
              <button
                onClick={handleDelete}
                className="absolute right-3 top-3 rounded-md bg-[rgba(255,255,255,0.1)] p-2 transition-colors hover:bg-[rgba(255,255,255,0.2)]"
                title="삭제"
              >
                <Trash2 size={16} className="text-white" />
              </button>
            )}
          </div>
        </>
      ) : (
        <>
          {/* <div className="relative flex h-full w-full flex-col items-center justify-center gap-[10px] bg-[#2E343D] p-5">
            <EyeOffIcon className="h-8 w-8 text-white" />
            <div className="text-center">
              <div className="mb-1 text-sm font-bold text-white">생성 실패</div>
              <div className="text-sm text-[#B6BBC6]">
                컨텐츠 정책에 위반된 것으로 보입니다.
              </div>
            </div>

            {onDelete && (
              <button
                onClick={handleDelete}
                className="absolute right-3 top-3 rounded-md bg-[rgba(255,255,255,0.1)] p-2 transition-colors hover:bg-[rgba(255,255,255,0.2)]"
                title="삭제"
              >
                <Trash2 size={16} className="text-white" />
              </button>
            )}
          </div> */}
        </>
      )}
      {getStatusBadge()}

      {/* 삭제 확인 모달 */}
      <DeleteConfirmModal
        isOpen={isDeleteConfirmOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        item={item}
      />
    </div>
  )
}
